import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API } from '../../utils/api';

/**
 * GDPR-style "Download My Data" panel.
 * Backend gathers all 8 data domains in parallel (asyncio.gather), so the
 * export finishes in ~1s instead of the previous serial ~3-5s.
 */
const DataExport = ({ showToast }) => {
  const { token } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [lastExport, setLastExport] = useState(null);
  const [exportType, setExportType] = useState('all');
  const [format, setFormat] = useState('json');

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/export/summary`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setSummary(data.summary || {});
        } else if (!cancelled) {
          setSummary({});
        }
      } catch (e) {
        if (!cancelled) setSummary({});
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Failsafe: never stay stuck in loading indefinitely. After 4 seconds
  // show the panel even if the summary endpoint hasn't responded — user can
  // still initiate an export, the stat cards just show zeros.
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setLoading((prev) => {
        if (prev) setSummary((s) => s || {});
        return false;
      });
    }, 4000);
    return () => clearTimeout(timeoutId);
  }, []);

  const humanBytes = (n) => {
    if (!n) return '0 B';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(2)} MB`;
  };

  const handleExport = async () => {
    setExporting(true);
    const t0 = performance.now();
    try {
      const endpoint = `/export/${exportType}?format=${format}`;
      const res = await fetch(`${API}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        showToast && showToast('Export failed. Please try again.', 'error');
        setExporting(false);
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = res.headers.get('Content-Disposition');
      let filename = `infopilot_export.${format}`;
      if (disposition) {
        const match = disposition.match(/filename=([^;]+)/);
        if (match) filename = match[1].trim();
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setLastExport({
        format,
        durationMs: Math.round(performance.now() - t0),
        size: blob.size,
        at: new Date(),
      });
      showToast && showToast('Your data is downloading ✨', 'success');
    } catch (e) {
      showToast && showToast('Export failed. Please try again.', 'error');
    }
    setExporting(false);
  };

  const cardStyle = {
    background: '#FFFFFF',
    border: '1px solid rgba(252, 211, 77, 0.35)',
    borderRadius: 16,
    padding: 24,
    boxShadow: '0 4px 20px rgba(252, 211, 77, 0.08)',
  };

  if (loading) {
    return (
      <div style={cardStyle} data-testid="data-export-loading">
        <div className="animate-pulse" style={{ color: '#047857' }}>
          Loading your data summary…
        </div>
      </div>
    );
  }

  const stats = [
    { label: 'Categories', value: summary?.categories ?? 0, tint: '#F59E0B' },
    { label: 'Search Results', value: summary?.search_results ?? 0, tint: '#0EA5E9' },
    { label: 'Templates', value: summary?.protocol_templates ?? 0, tint: '#10B981' },
    { label: 'XP Earned', value: summary?.xp ?? 0, tint: '#047857' },
  ];

  return (
    <div style={cardStyle} data-testid="data-export-panel">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12,
          background: 'linear-gradient(135deg, #FDE68A, #FCD34D)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#0F172A', fontSize: 20,
        }}>
          <i className="fas fa-download" />
        </div>
        <div>
          <h3 style={{
            fontFamily: 'Outfit, "IBM Plex Sans", sans-serif',
            fontSize: '1.15rem', fontWeight: 700, color: '#0F172A', margin: 0,
          }}>
            Download My Data
          </h3>
          <div style={{ fontSize: '0.8rem', color: '#64748B', marginTop: 2 }}>
            Your right to data portability — powered by parallel fan-out, usually under 1&nbsp;second
          </div>
        </div>
      </div>

      <div style={{
        background: 'rgba(16, 185, 129, 0.08)',
        border: '1px solid rgba(16, 185, 129, 0.25)',
        borderRadius: 10, padding: '10px 14px', margin: '16px 0 18px',
        color: '#047857', fontSize: '0.85rem', lineHeight: 1.5,
      }}>
        <strong>GDPR / CCPA friendly.</strong> Download every byte we have about you —
        categories, search history, protocols, friends, XP, the lot. Nothing is omitted.
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: 10, marginBottom: 20,
      }} data-testid="data-export-summary">
        {stats.map((s) => (
          <div key={s.label} style={{
            background: '#FDFBF7',
            border: '1px solid rgba(252, 211, 77, 0.25)',
            borderRadius: 10, padding: 12, textAlign: 'center',
          }}>
            <div style={{
              fontFamily: 'Outfit, sans-serif',
              fontSize: '1.6rem', fontWeight: 800, color: s.tint,
              lineHeight: 1,
            }}>
              {s.value}
            </div>
            <div style={{ fontSize: '0.72rem', color: '#64748B', marginTop: 4 }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <label style={{ display: 'block', fontSize: '0.82rem', color: '#475569', fontWeight: 600, marginBottom: 6 }}>
            What to export
          </label>
          <select
            value={exportType}
            onChange={(e) => setExportType(e.target.value)}
            data-testid="export-type-select"
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 10,
              border: '1px solid rgba(252, 211, 77, 0.35)',
              background: '#FDFBF7', color: '#0F172A', fontSize: '0.95rem',
              outline: 'none',
            }}
          >
            <option value="all">Everything (recommended)</option>
            <option value="categories">Categories only</option>
            <option value="search-results">Search results only</option>
            <option value="protocols">Protocols only</option>
          </select>
        </div>

        <div>
          <label style={{ display: 'block', fontSize: '0.82rem', color: '#475569', fontWeight: 600, marginBottom: 6 }}>
            Format
          </label>
          <div style={{ display: 'flex', gap: 10 }}>
            {['json', 'csv'].map((f) => (
              <button
                key={f}
                onClick={() => setFormat(f)}
                data-testid={`export-format-${f}`}
                style={{
                  flex: 1, padding: '10px 14px', borderRadius: 10,
                  cursor: 'pointer', fontWeight: 600,
                  background: format === f ? 'rgba(253, 230, 138, 0.18)' : '#FDFBF7',
                  border: format === f ? '2px solid #FCD34D' : '2px solid #E2E8F0',
                  color: format === f ? '#047857' : '#475569',
                  transition: 'all 0.15s',
                }}
              >
                {f === 'json' ? 'JSON — detailed' : 'CSV — spreadsheet'}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={handleExport}
          disabled={exporting}
          data-testid="export-download-btn"
          style={{
            width: '100%', padding: '14px', borderRadius: 999, border: 'none',
            background: exporting
              ? 'linear-gradient(135deg, #FEF08A, #FDE047)'
              : 'linear-gradient(135deg, #FDE68A, #FCD34D)',
            color: '#0F172A', fontSize: '1rem', fontWeight: 800,
            cursor: exporting ? 'wait' : 'pointer',
            boxShadow: '0 8px 20px rgba(252, 211, 77, 0.3)',
            transition: 'transform 0.1s, box-shadow 0.2s',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          }}
        >
          {exporting ? (
            <>
              <i className="fas fa-circle-notch fa-spin" />
              Preparing your data…
            </>
          ) : (
            <>
              <i className="fas fa-download" />
              Download my data
            </>
          )}
        </button>

        {lastExport && !exporting && (
          <div data-testid="last-export-info" style={{
            padding: '10px 14px',
            background: 'rgba(16, 185, 129, 0.08)',
            border: '1px solid rgba(16, 185, 129, 0.25)',
            borderRadius: 10,
            color: '#047857', fontSize: '0.82rem',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
            flexWrap: 'wrap',
          }}>
            <span>
              ✓ Downloaded <strong>{lastExport.format.toUpperCase()}</strong>
              {' '}·{' '}
              <strong>{humanBytes(lastExport.size)}</strong>
              {' '}·{' '}
              prepared in <strong>{lastExport.durationMs} ms</strong>
            </span>
            <span style={{ color: '#059669', fontSize: '0.75rem' }}>
              (parallel fan-out 🔀)
            </span>
          </div>
        )}
      </div>

      <p style={{
        fontSize: '0.72rem', color: '#94A3B8',
        marginTop: 14, textAlign: 'center',
      }}>
        Your data stays yours. We never share it, and you can re-download any time.
      </p>
    </div>
  );
};

export default DataExport;
